import { useReducer, useCallback } from 'react'
import * as R from 'ramda'
import effects from 'app/effects/effects'
import moment from 'moment'

const TOGGLE_SHOW_COMMENTS = 'TOGGLE_SHOW_COMMENTS'
const TOGGLE_SHOW_METRICS = 'TOGGLE_SHOW_METRICS'
const START_LOADING = 'START_LOADING'
const SET_NOTIFICATION = 'SET_NOTIFICATION'
const SET_LANGUAGES = 'SET_LANGUAGES'
const SET_NOTIFICATION_METRICS = 'SET_NOTIFICATION_METRICS'
const SET_ERROR = 'SET_ERROR'

const initialState = {
  notification: undefined,
  notiLanguages: undefined,
  isLoading: false,
  isShowComments: false,
  isShowMetrics: false,
  isShowLanguages: false,
  error: undefined,
}

function reducer(state, action) {
  switch (action.type) {
    case SET_NOTIFICATION:
      return R.mergeRight(state, {
        notification: R.mergeRight(
          state.notification,
          R.over(
            R.lensProp('subscribers'),
            R.defaultTo([]),
            action.notification,
          ),
        ),
        isLoading: false,
        error: undefined,
      })
    case SET_LANGUAGES: {
      return R.mergeRight(state, {
        notiLanguages: action.notiLanguages.data,

        isLoading: false,
        error: undefined,
      })
    }
    case SET_NOTIFICATION_METRICS: {
      return R.mergeRight(state, {
        notification: R.mergeRight(state.notification, action.metrics),
        isLoading: false,
        error: undefined,
      })
    }
    case SET_ERROR:
      return R.mergeRight(state, {
        notification: undefined,
        isLoading: false,
        error: action.error,
      })
    case START_LOADING:
      return R.mergeRight(state, {
        isLoading: true,
        error: undefined,
      })
    case TOGGLE_SHOW_COMMENTS: {
      return R.mergeRight(state, {
        isShowComments: !state.isShowComments,
        isShowMetrics: false,
      })
    }
    case TOGGLE_SHOW_METRICS: {
      return R.mergeRight(state, {
        isShowComments: false,
        isShowMetrics: !state.isShowMetrics,
      })
    }
    default:
      return state
  }
}

const sortByCreatedAt = R.sortBy(
  R.compose((date) => moment(date).unix(), R.prop('createdAt')),
)

export const useNotificationInfo = (id) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const loadNotificationWhenNil = () => {
    if (!R.isNil(R.path(['notification', 'id'], state))) return
    loadNotification()
  }
  const loadNotificationMetricsWhenNil = () => {
    if (!R.isNil(R.path(['notification', 'analytics'], state))) return
    loadNotificationMetrics()
  }

  const loadNotification = useCallback(() => {
    if (state.isLoading) return
    dispatch({ type: START_LOADING })
    return effects.apis
      .getNotificationById(id)
      .then(
        R.compose(
          dispatch,
          R.mergeRight({ type: SET_NOTIFICATION }),
          R.objOf('notification'),
        ),
      )
      .catch(
        R.compose(
          dispatch,
          R.mergeRight({ type: SET_ERROR }),
          R.objOf('error'),
        ),
      )
  }, [id, dispatch, state.isLoading])

  const loadNotificationLanguages = useCallback(() => {
    if (state.isLoading) return
    dispatch({ type: START_LOADING })
    return effects.apis
      .getNotificationLanguage(id)
      .then(
        R.compose(
          dispatch,
          R.mergeRight({ type: SET_LANGUAGES }),
          R.objOf('notiLanguages'),
        ),
      )
      .catch(
        R.compose(
          dispatch,
          R.mergeRight({ type: SET_ERROR }),
          R.objOf('error'),
        ),
      )
  }, [id, dispatch, state.isLoading])

  const loadNotificationMetrics = useCallback(() => {
    if (state.isLoading) return
    dispatch({ type: START_LOADING })
    return effects.apis
      .getNotificationMetrics(id)
      .then(
        R.compose(
          dispatch,
          R.mergeRight({ type: SET_NOTIFICATION_METRICS }),
          R.objOf('metrics'),
        ),
      )
      .catch(
        R.compose(
          dispatch,
          R.mergeRight({ type: SET_ERROR }),
          R.objOf('error'),
        ),
      )
  }, [id, dispatch, state.isLoading])

  const refetchNotification = useCallback(() => {
    if (state.isLoading) return
    loadNotification()
    loadNotificationMetrics()
  }, [loadNotification, loadNotificationMetrics, state.isLoading])

  const handleToggleShowComments = () => {
    dispatch({ type: TOGGLE_SHOW_COMMENTS })
    loadNotificationWhenNil()
  }
  const handleToggleShowMetrics = () => {
    dispatch({ type: TOGGLE_SHOW_METRICS })
    loadNotificationMetricsWhenNil()
  }
  const fetchLanguages = () => {
    loadNotificationLanguages()
  }

  const comments = R.compose(
    R.unless(
      R.isNil,
      R.compose(
        R.map(R.over(R.lensProp('replies'), sortByCreatedAt)),
        sortByCreatedAt,
      ),
    ),
    R.path(['notification', 'comments']),
  )(state)

  return {
    comments,
    commentsCount: R.unless(R.isNil, commentsToCommentCount, comments),
    languagesWithSubscribersCount: R.path(['notiLanguages'], state),
    metrics: R.path(['notification', 'analytics'], state),
    metricsAudioFullListened: R.path(
      ['notification', 'audioFullListened'],
      state,
    ),
    metricsAudioListened: R.path(['notification', 'audioListened'], state),
    onToggleShowComments: handleToggleShowComments,
    onToggleShowMetrics: handleToggleShowMetrics,
    translations: R.path(['notification', 'translations'], state),
    notiLanguages: R.path(['notiLanguages'], state),
    fetchLanguages,
    refetchNotification,
    isLoading: state.isLoading,
    isShowComments: state.isShowComments,
    isShowMetrics: state.isShowMetrics,
    error: state.error,
  }
}

const commentsToCommentCount = R.reduce(
  (acc, comment) =>
    acc +
    1 +
    R.reduce((acc, _) => acc + 1, 0, R.propOr([], 'replies', comment)),
  0,
)
